import { useState, useEffect } from 'react'
import { cloneDeep } from 'lodash'

import { useAppSelector } from 'redux/hooks'
import { selectCardMap, selectCategories } from 'redux/deck/selectors'

import { ColorQuantity, getCastingCostColors, getDeckColorIdent, getManaProductionData } from 'utils/mtgColor.utils'

import { CardType, EDH_FORMATS } from 'types/deck'
import { ColorAbr } from 'services/apiTypes/card.types'

export type Options = {
  targetLandCount: number
  preferSnow: boolean
  requireAtLeastOneBasicPerColor: boolean
}

export const DEFAULT_USER_OPTIONS = {
  targetLandCount: 36,
  preferSnow: false,
  requireAtLeastOneBasicPerColor: true,
}

type recsData = {
  recommendedQty: number
  currentQty: number
  percentOfCosts: number
  percentOfProduction: number
  distance: number
  weight: number
}
const DEFAULT_RECS_DATA = {
  recommendedQty: 0,
  currentQty: 0,
  percentOfCosts: 0,
  percentOfProduction: 0,
  distance: 0,
  weight: 0,
}

type landRecsMapType = Record<ColorAbr, recsData>
const DEFAULT_LAND_RECS_MAP: landRecsMapType = {
  W: cloneDeep(DEFAULT_RECS_DATA),
  U: cloneDeep(DEFAULT_RECS_DATA),
  B: cloneDeep(DEFAULT_RECS_DATA),
  R: cloneDeep(DEFAULT_RECS_DATA),
  G: cloneDeep(DEFAULT_RECS_DATA),
  C: cloneDeep(DEFAULT_RECS_DATA),
}

const DEFAULT_BASIC_LANDS_IN_DECK: Record<ColorAbr, number> = {
  W: 0,
  U: 0,
  B: 0,
  R: 0,
  G: 0,
  C: 0,
}

export const COLOR_TO_BASIC_MAP: Record<ColorAbr, string> = {
  W: 'Plains',
  U: 'Island',
  B: 'Swamp',
  R: 'Mountain',
  G: 'Forest',
  C: 'Wastes',
}

export const BASIC_TO_COLOR_MAP: Record<string, ColorAbr> = {
  'Plains': 'W',
  'Island': 'U',
  'Swamp': 'B',
  'Mountain': 'R',
  'Forest': 'G',
  'Wastes': 'C',
  'Snow-Covered Plains': 'W',
  'Snow-Covered Island': 'U',
  'Snow-Covered Swamp': 'B',
  'Snow-Covered Mountain': 'R',
  'Snow-Covered Forest': 'G',
  'Snow-Covered Wastes': 'C',
}

type ManaBaseDeckData = {
  castingCostColorData: ColorQuantity
  nonBasicLandCount: number
  basicLandCount: number
  basicLandNamesInColorId: Record<string, boolean>
  landRecsMap: landRecsMapType
}

const useGetManaBaseDeckData = (options: Options): ManaBaseDeckData => {
  const [castingCostColorData, setCastingCostColorData] = useState<ColorQuantity>({})
  const [nonBasicLandCount, setNonBasicLandCount] = useState(0)
  const [basicLandCount, setBasicLandCount] = useState(0)
  const [basicLandNamesInColorId, setBasicLandNamesInColorId] = useState<Record<string, boolean>>({})
  const [landRecsMap, setLandRecsMap] = useState(DEFAULT_LAND_RECS_MAP)

  const cardMap = useAppSelector(selectCardMap)
  const categories = useAppSelector(selectCategories)
  const format = useAppSelector(state => state.deck.format)

  useEffect(() => {
    const deck = Object.values(cardMap)
    const colorIdent = getDeckColorIdent(deck, categories, EDH_FORMATS.includes(format))
    const updatedBasicLandNamesInColorId: Record<string, boolean> = {}
    const updatedLandRecsMap = cloneDeep(DEFAULT_LAND_RECS_MAP)

    const { basicLandsInDeck, basicLandCount, nonBasicLandsInDeck, nonBasicLandCount } = deck.reduce(
      (acc, card) => {
        if (!categories[card.categories[0]]?.includedInDeck) return acc // Ignore out of deck cards
        if (!card.types.includes('Land')) return acc // Ignore non-lands

        if (card.superTypes.includes('Basic')) {
          acc.basicLandsInDeck[BASIC_TO_COLOR_MAP[card.name]] += card.qty
          acc.basicLandCount += card.qty
        } else {
          acc.nonBasicLandsInDeck.push(card)
          acc.nonBasicLandCount += card.qty
        }

        return acc
      },
      {
        basicLandsInDeck: cloneDeep(DEFAULT_BASIC_LANDS_IN_DECK),
        basicLandCount: 0,
        nonBasicLandsInDeck: new Array<CardType>(),
        nonBasicLandCount: 0,
      },
    )

    if (colorIdent.length == 0) colorIdent.push('C') // colorIdent is an emtpy array when the deck is colorless

    // Save what basic lands are in this deck's color identity (used for display purposes)
    for (const colorAbr of colorIdent) {
      updatedBasicLandNamesInColorId[colorAbr] = true
    }

    // target basic land count = target land count - (options.requireAtLeastOneBasicPerColor ? total colors  : 0) - non basic lands in deck
    const targetBasicLandCount =
      options.targetLandCount - nonBasicLandCount - (options.requireAtLeastOneBasicPerColor ? colorIdent.length : 0)
    const manaCostData = getCastingCostColors(deck, categories)
    const manaProductionData = getManaProductionData(nonBasicLandsInDeck, categories, false, colorIdent)

    // acts as an accumulator for calculating the percentage offset of each basic land in the deck
    // eg. for a UBR deck, each color is assigned a weight based on the difference between each color's cost ratio and production ratio
    //    The ratio of that weight to the total of weights (sumColorOffset) is how many basic lands should be included of each color
    //    eg. U has a weight of 1, B of 2, R of 4. There should be twice as many Mountains as Swamps, and twice as many Swamps as Islands
    let sumColorOffset = 0

    for (const color of Object.keys(updatedLandRecsMap)) {
      const colorAbr = color as ColorAbr

      updatedLandRecsMap[colorAbr].percentOfCosts = manaCostData.colors[colorAbr] / manaCostData.totalPips
      updatedLandRecsMap[colorAbr].percentOfProduction =
        manaProductionData.colors[colorAbr] / (manaProductionData.totalPips + targetBasicLandCount)
      updatedLandRecsMap[colorAbr].distance =
        updatedLandRecsMap[colorAbr].percentOfCosts - updatedLandRecsMap[colorAbr].percentOfProduction
      updatedLandRecsMap[colorAbr].weight =
        updatedLandRecsMap[colorAbr].distance > 0 ? Math.round(updatedLandRecsMap[colorAbr].distance * 100) : 0

      sumColorOffset += updatedLandRecsMap[colorAbr].weight
    }

    // Looping over the same thing twice, because now we need to use sumColorOffset to calculate the actual quantities
    for (const color of Object.keys(updatedLandRecsMap)) {
      const colorAbr = color as ColorAbr

      if (!colorIdent.includes(colorAbr)) continue

      updatedLandRecsMap[colorAbr].recommendedQty =
        Math.round((updatedLandRecsMap[colorAbr].weight / sumColorOffset) * targetBasicLandCount) +
        (options.requireAtLeastOneBasicPerColor ? 1 : 0)
      updatedLandRecsMap[colorAbr].currentQty = basicLandsInDeck[colorAbr]
    }

    setCastingCostColorData(manaCostData.colors)
    setNonBasicLandCount(nonBasicLandCount)
    setBasicLandNamesInColorId(updatedBasicLandNamesInColorId)
    setBasicLandCount(basicLandCount)

    // Make sure the recommended count isn't over or under
    // This happens when an odd number of basic lands are distributed over an even number (or an even number over an odd number) eg. 37 basic lands in an two color deck
    // And the distribution of colors is even. Each basic land type rounds up or down, which results in too few or too many
    const numberOfLandsAdded = Object.values(updatedLandRecsMap).reduce(
      (acc, color) => (acc += color.recommendedQty),
      0,
    )
    const landCountMatchesTargetLandCount =
      numberOfLandsAdded == targetBasicLandCount + (options.requireAtLeastOneBasicPerColor ? colorIdent.length : 0)

    // Note: land counts match, returning early. Nothing else to do
    if (landCountMatchesTargetLandCount) return setLandRecsMap(updatedLandRecsMap)

    // this value is either positive or negative depending on if we need to increase or decrease the number of lands in the deck to match the target land count
    let missingBasicCount =
      targetBasicLandCount + (options.requireAtLeastOneBasicPerColor ? colorIdent.length : 0) - numberOfLandsAdded

    // Looping over colors because no any one basic should be off by more than one
    // this loop will run N times where N = abs(missingBasicLandCount)
    for (const colorAbr of Object.keys(updatedLandRecsMap)) {
      if (missingBasicCount !== 0) break
      if (!basicLandNamesInColorId[colorAbr]) continue

      const addOrSubtract = missingBasicCount / Math.abs(missingBasicCount) // 1 or -1 depending on if there are more or fewer basics than needed

      updatedLandRecsMap[colorAbr as ColorAbr].recommendedQty += 1 * addOrSubtract
      missingBasicCount -= 1 * addOrSubtract
    }

    setLandRecsMap(updatedLandRecsMap)
  }, [cardMap, categories, format, options])

  return {
    castingCostColorData,
    nonBasicLandCount,
    basicLandCount,
    basicLandNamesInColorId,
    landRecsMap,
  }
}

export default useGetManaBaseDeckData
